import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';

import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { firstValueFrom, forkJoin } from 'rxjs';

import { SETTINGS, Settings } from '../../../app.settings';

import { notifyAndTrackError } from '../../../common/error';
import { isEmpty } from '../../../common/functions';
import { successMsg } from '../../../common/messages';

import { AuditInfoComponent, CanComponentDeactivate, ProcessingComponent, UnsavedChangesDialogComponent } from '../../../components';

import { AppInsightsService } from '../../../services/app-insights.service';
import { IPermits, UploadService } from '../../../services/insecta';
import { PermitsAdminService } from '../../../services/insecta/admin/permitslist-admin.service';
import { MSGraphService } from '../../../services/msgraph.service';
import { FileUploadModule } from 'primeng/fileupload';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-permits-admin',
  standalone: true,
  imports: [
    BreadcrumbModule,
    ButtonModule,
    CardModule,
    CheckboxModule,
    CommonModule,
    DialogModule,
    FloatLabelModule,
    FormsModule,
    InputTextModule,
    RouterModule,
    TableModule,
    ToggleSwitchModule,
    AuditInfoComponent,
    ProcessingComponent,
    UnsavedChangesDialogComponent,
    FileUploadModule,
    TooltipModule
  ],

  templateUrl: 'permitslist-admin.component.html'
})
export class PermitsListAdminComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  data: IPermits[] | any = [];
  modifiedData: { [s: string]: IPermits } = {};
  isFetchingPdf = false;

  dialogConfirmSub: ((leave: boolean) => void) | null = null;

  dialogVars: any = {
    visible: false,
    data: <IPermits>{}
  };

  pageVars: any = {
    currentUser: ``,
    isEditing: false,
    isReady: false,
    showUnsavedChangesDialog: false,
    pageId: `Admin:Permits`,
    breadcrumbItems: <MenuItem[]>[{ label: 'Global Lists', routerLink: '/admin/globallists' }, { label: 'Permits Management' }]
  };

  constructor(
    private apiService: PermitsAdminService,
    private appInsights: AppInsightsService,
    private graphService: MSGraphService,
    private msgService: MessageService,
    private router: Router,
    private uploadService: UploadService,
    @Inject(SETTINGS) private settings: Settings
  ) {
    this.pageVars.pageId = `${settings.environment}:Admin:Permits`;
    appInsights.trackPageView(this.pageVars.pageId, `/admin/permits`);
    this.dialogVars.data = <IPermits>{};
  }

  canDeactivate(): Promise<boolean> {
    if (!this.pageVars.isEditing) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      this.pageVars.showUnsavedChangesDialog = true;

      const confirmSub = (decision: boolean) => {
        this.dialogConfirmSub = null;
        resolve(decision);
      };

      this.dialogConfirmSub = confirmSub;
    });
  }

  onDialogConfirm(leave: boolean) {
    this.pageVars.showUnsavedChangesDialog = false;
    if (this.dialogConfirmSub) {
      this.dialogConfirmSub(leave);
    }
  }

  ngOnDestroy() {
    this.data = [];
    this.pageVars = {
      isReady: false
    };
  }

  ngOnInit() {
    this.appInsights.trackEvent(`${this.pageVars.pageId}:Init`);

    const request = {
      profile: this.graphService.getMyProfile$(),
      permits: this.apiService.getPermitsAll()
    };

    firstValueFrom(forkJoin(request))
      .then((response) => {
        this.data = response.permits;
        this.pageVars.currentUser = response.profile.userPrincipalName;
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, {
          component: this.pageVars.pageId,
          action: 'Init:Error'
        });
      })
      .finally(() => {
        this.pageVars.isReady = true;
        this.pageVars.isEditing = false;
      });
  }

  backClick() {
    this.router.navigate(['admin/globallists']);
  }

  createDlgCancel() {
    this.dialogVars.visible = false;
    this.dialogVars.data = <IPermits>{};
  }

  createDlgSave() {
    this.dialogVars.data.action = `INS`;
    this.dialogVars.data.actionBy = this.pageVars.currentUser;
    this.dialogVars.data.actionOn = new Date(Date.now());

    this.appInsights.trackEvent(`${this.pageVars.pageId}:Grid:Insert`, { Permit: this.dialogVars.data });

    firstValueFrom(this.apiService.addPermits(this.dialogVars.data))
      .then((result) => {
        this.dialogVars.data.id = result;
        this.data = [this.dialogVars.data, ...this.data];

        successMsg(this.msgService, 'Permits has been created');

        this.dialogVars.visible = false;
        this.dialogVars.data = <IPermits>{};
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, {
          component: this.pageVars.pageId,
          action: 'Grid:Insert:Error'
        });
      });
  }

  dialogIsValid(): boolean {
    const nameValue = this.dialogVars?.data?.name?.trim() ?? '';
    if (isEmpty(nameValue)) return false;
    if (nameValue === '') return false;

    return true;
  }

  gridNewRow() {
    this.dialogVars.data = <IPermits>{
      isActive: true,
      actionBy: this.pageVars.currentUser
    };

    this.dialogVars.visible = true;
  }

  gridRowEditCancel(c: IPermits, idx: number) {
    this.data[idx] = this.modifiedData[c.id as string];
    delete this.modifiedData[c.id as string];
    this.pageVars.isEditing = false;
  }

  gridRowEditInit(c: IPermits) {
    this.pageVars.isEditing = true;
    this.modifiedData[c.id as string] = { ...c };
    c.action = 'UPD';
    c.actionBy = this.pageVars.currentUser;
    c.actionOn = new Date(Date.now());
  }

  gridRowEditSave(c: IPermits, idx: number) {
    this.pageVars.isEditing = true;
    this.pageVars.isReady = false;

    this.appInsights.trackEvent(`${this.pageVars.pageId}:Grid:Save`, { Permit: c });
    firstValueFrom(this.apiService.updatePermits(c))
      .then(() => {
        successMsg(this.msgService, 'Permit is updated');
      })
      .catch((err) => {
        this.data[idx] = this.modifiedData[c.id as string];
        notifyAndTrackError(this.msgService, this.appInsights, err, {
          component: this.pageVars.pageId,
          action: 'Grid:Save:Error'
        });
      })
      .finally(() => {
        delete this.modifiedData[c.id as string];
        this.pageVars.isReady = true;
        this.pageVars.isEditing = false;
      });
  }

  toggleStatus(item: IPermits) {
    firstValueFrom(this.apiService.updatePermits(item))
      .then(() => {
        const idx = this.data.findIndex((x: IPermits) => x.id == item.id);
        this.data[idx].isActive = item.isActive;
        this.data[idx].action = 'UPD';
        this.data[idx].actionBy = this.pageVars.currentUser;
        this.data[idx].actionOn = new Date();
        successMsg(this.msgService, 'Permits is updated');
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, {
          component: this.pageVars.pageId,
          action: 'ToggleStatus:Error'
        });
      });
  }

  async onFileUpload(event: any, index: number = 9999) {
    const file: File = event.files[0];
    const fileName = file.name;
    this.pageVars.isReady = false;

    firstValueFrom(this.uploadService.uploadImage(file))
      .then((uploadId: string) => {
        this.data[index].fileName = fileName;
        this.data[index].uploadId = uploadId;
        this.appInsights.trackEvent(`${this.pageVars.pageId}:FileUpload`, { fileName: file.name, uploadId: uploadId });
        this.gridRowEditSave(this.data[index], index);
        successMsg(this.msgService, 'File uploaded successfully');
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'InsectAdminComponent', action: 'FileUpload:Error' });
      })
      .finally(() => {
        this.pageVars.isReady = true;
      });
  }

  onFileUploadError(event: any) {
    console.log(event);
  }

  public downloadPdf(permit: IPermits | any) {
    if (isEmpty(permit.uploadId)) return;

    this.isFetchingPdf = true;
    firstValueFrom(this.uploadService.getImage(permit.uploadId as string, true))
      .then((pdfUrl: string) => {
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = permit.fileName || 'document';
        link.click();
        URL.revokeObjectURL(pdfUrl);
        this.isFetchingPdf = false;
      })
      .catch((err) => {
        this.isFetchingPdf = false;
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'PermitsListAdminComponent', action: 'DownloadPdf:Error' });
        notifyAndTrackError(this.msgService, this.appInsights, 'Failed to download document', {
          component: 'PermitsListAdminComponent',
          action: 'DownloadPdf:Message'
        });
      });
  }

  deletePdf(permit: IPermits | any, index: number) {
    permit.uploadId = null;
    permit.fileName = '';
    this.data[index] = permit;
    this.gridRowEditSave(this.data[index], index);
  }
}
